import { BrowserRouter, Navigate, useRoutes, useLocation } from 'react-router-dom'
import { useMemo, useEffect, useState } from 'react'
import { useAuthStore } from '@/store/auth'
import { clearPostLoginRedirect, peekPostLoginRedirect } from '@/lib/auth/postLoginRedirect'
import { generateRoutesFromUserRoutes } from '@/routes/generateRoutes'
import { HelmetProvider } from 'react-helmet-async'
import { Providers } from '@/components/providers/Providers'
import { SessionProvider } from '@/components/providers/SessionProvider'
import type { RouteObject } from 'react-router-dom'

// Public routes (no authentication required)
import Login from '@/pages/Login'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import TwoFactor from '@/pages/TwoFactor'
import TwoFactorSetup from '@/pages/TwoFactor/Setup'
import Dashboard from '@/pages/Dashboard'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { UnauthenticatedRedirect } from '@/components/auth/UnauthenticatedRedirect'

// Import routes with multiple dynamic segments that won't come from user_routes
import EditBudgetManagementPage from '@/pages/BudgetManagement/Edit'
import SendCustomEmailPage from '@/pages/ShippingLinesManagement/FinancialStatements/SendCustomEmail'

const BASE_PUBLIC_ROUTES: RouteObject[] = [
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/two-factor', element: <TwoFactor /> },
  { path: '/two-factor/setup', element: <TwoFactorSetup /> },
]

// Additional authenticated routes (routes with multiple dynamic segments not from user_routes)
const ADDITIONAL_AUTH_ROUTES: RouteObject[] = [
  {
    path: '/budget-management/:expenseType/:sourceId',
    element: (
      <AuthGuard>
        <EditBudgetManagementPage />
      </AuthGuard>
    ),
  },
  {
    path: '/shipping-lines/financial-statements/:shippingLineId/send-email',
    element: (
      <AuthGuard>
        <SendCustomEmailPage />
      </AuthGuard>
    ),
  },
]

// Default authenticated routes (fallback when user_routes not loaded yet)
const DEFAULT_AUTH_ROUTES: RouteObject[] = [
  ...BASE_PUBLIC_ROUTES,
  ...ADDITIONAL_AUTH_ROUTES,
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <Dashboard />
      </AuthGuard>
    ),
  },
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  // Don't include catch-all here - let the actual route generation handle it
  // This prevents redirecting to dashboard when reloading on other pages
]

const UNAUTH_ROUTES: RouteObject[] = [
  ...BASE_PUBLIC_ROUTES,
  { path: '/', element: <Login /> },
  { path: '*', element: <UnauthenticatedRedirect /> },
]

function AppRoutes({ routes }: { routes: RouteObject[] }) {
  const element = useRoutes(routes)
  return element
}

function AppContent() {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const isLoading = useAuthStore((state) => state.isLoading)
  const token = useAuthStore((state) => state.token)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const initializeAuth = useAuthStore((state) => state.initializeAuth)
  const [isInitialized, setIsInitialized] = useState(false)
  
  // Check if we're on a public route (login, forgot password, etc.)
  const isPublicRoute = ['/login', '/forgot-password', '/reset-password', '/two-factor'].some(
    route => location.pathname === route || location.pathname.startsWith(route)
  )
  
  // Initialize auth on mount and wait for it to complete
  useEffect(() => {
    let isMounted = true
    const init = async () => {
      try {
        await initializeAuth()
        if (isMounted) {
          setIsInitialized(true)
          if (import.meta.env.DEV) {
            console.log('[App] Auth initialization complete')
          }
        }
      } catch (error) {
        console.error('[App] Auth initialization failed:', error)
        if (isMounted) {
          setIsInitialized(true) // Still set to true to prevent infinite loading
        }
      }
    }
    init()
    return () => {
      isMounted = false
    }
  }, [initializeAuth])

  // Drop saved deeplink once the user has landed on that URL while authenticated (login flow uses consume(); this covers already-logged-in opens).
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return
    const stored = peekPostLoginRedirect()
    if (!stored) return
    const current = `${location.pathname}${location.search}${location.hash}`
    if (current === stored) {
      clearPostLoginRedirect()
    }
  }, [isInitialized, isAuthenticated, location.pathname, location.search, location.hash])
  
  // Memoize routes to prevent unnecessary regenerations
  // Wait for initialization to complete before generating routes
  const routes = useMemo(() => {
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('[App] Generating routes:', {
        isInitialized,
        hasToken: !!token,
        isAuthenticated,
        hasUser: !!user,
        hasUserRoutes: user?.user_routes !== undefined,
        userRoutesLength: user?.user_routes?.length || 0
      })
    }

    //sarVNesxc!C@y7G0!HMG
    
    // Show public routes while initializing (but don't show loading screen if we have token)
    if (!isInitialized) {
      // If we have a token in localStorage, show default auth routes to prevent redirect loop
      if (token) {
        if (import.meta.env.DEV) {
          console.log('[App] Using DEFAULT_AUTH_ROUTES (not initialized, but has token)')
        }
        return DEFAULT_AUTH_ROUTES
      }
      if (import.meta.env.DEV) {
        console.log('[App] Using BASE_PUBLIC_ROUTES (not initialized, no token)')
      }
      return BASE_PUBLIC_ROUTES
    }
    
    // After initialization: if no token, always redirect to login
    if (!token) {
      if (import.meta.env.DEV) {
        console.log('[App] No token after initialization, using UNAUTH_ROUTES (redirect to login)')
      }
      return UNAUTH_ROUTES
    }
    
    // If we have a token and are authenticated, try to generate authenticated routes
    if (isAuthenticated && (token || user)) {
      // Get routes from user object, with fallback to localStorage if user_routes is undefined
      let routesToUse: any[] = []
      
      if (user?.user_routes !== undefined) {
        // Use routes from user object (guaranteed to be an array from auth store)
        routesToUse = Array.isArray(user.user_routes) ? user.user_routes : []
      } else if (token && typeof window !== 'undefined') {
        // Fallback: try to get routes from localStorage during initialization
        try {
          const authStorage = localStorage.getItem('auth-storage')
          if (authStorage) {
            const parsed = JSON.parse(authStorage)
            const storedUser = parsed.state?.user
            if (storedUser?.user_routes && Array.isArray(storedUser.user_routes)) {
              routesToUse = storedUser.user_routes
              if (import.meta.env.DEV) {
                console.log('[App] Using routes from localStorage (user_routes not yet loaded):', {
                  routeCount: routesToUse.length
                })
              }
            }
          }
        } catch (error) {
          console.error('[App] Error reading routes from localStorage:', error)
        }
      }
      
      // Generate routes (even if empty array - will still include dashboard)
      const generatedRoutes = generateRoutesFromUserRoutes(
        routesToUse,
        [...BASE_PUBLIC_ROUTES, ...ADDITIONAL_AUTH_ROUTES]
      )
      
      if (import.meta.env.DEV) {
        console.log('[App] Generated routes:', {
          userRoutesCount: routesToUse.length,
          generatedRoutesCount: generatedRoutes.length,
          routePaths: generatedRoutes.map(r => r.path),
          hasUser: !!user,
          userRoutesDefined: user?.user_routes !== undefined,
          source: user?.user_routes !== undefined ? 'user' : 'localStorage'
        })
      }
      
      return generatedRoutes
    }
    
    // Token exists but not authenticated (e.g., invalid/expired token), redirect to login
    if (import.meta.env.DEV) {
      console.log('[App] Token exists but not authenticated, using UNAUTH_ROUTES (redirect to login)')
    }
    return UNAUTH_ROUTES
  }, [user?.user_routes, token, user, isInitialized, isAuthenticated])

  return (
    <SessionProvider>
      {/* Show loading overlay only if we're initializing and don't have a token yet */}
      {/* Don't show full-page loader on public routes (login, etc.) - let the form handle its own loading state */}
      {/* If we have a token, render routes immediately (even if not fully initialized) */}
      {/* This prevents redirecting away from the current page on reload */}
      {(!isInitialized || isLoading) && !token && !isPublicRoute ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div>Loading...</div>
        </div>
      ) : (
        <AppRoutes routes={routes} />
      )}
    </SessionProvider>
  )
}

function App() {
  return (
    <HelmetProvider>
      <Providers>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </BrowserRouter>
      </Providers>
    </HelmetProvider>
  )
}

export default App

